import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于天天影视',
  description: '天天影视致力于提供最新最全的综艺节目在线观看服务。热门综艺、真人秀、脱口秀，高清免费，天天更新。',
  url: 'https://tiantian.tv/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '天天影视',
    url: 'https://tiantian.tv',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '天天影视是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天天影视是一个专注于综艺节目的在线观看平台，提供热门综艺、真人秀、脱口秀、音乐综艺、竞技综艺等各类综艺节目的高清在线观看服务。',
      },
    },
    {
      '@type': 'Question',
      name: '天天影视的综艺节目是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，天天影视提供免费的综艺节目在线观看服务。我们致力于让每一位用户都能轻松享受优质的综艺内容，无需付费即可观看高清综艺节目。',
      },
    },
    {
      '@type': 'Question',
      name: '天天影视的综艺节目多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天天影视每天都会更新最新的综艺节目内容。热门综艺节目一般在上线后24小时内即可在天天影视观看，确保用户不会错过任何精彩内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系天天影视的客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过以下方式联系我们：邮箱contact@tiantian.tv，客服电话400-888-6666（工作日9:00-18:00），或通过官方微博和微信公众号留言。',
      },
    },
    {
      '@type': 'Question',
      name: '天天影视支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天天影视支持电脑、平板和手机等多种设备在线观看。只需使用浏览器访问天天影视网站，即可随时随地享受综艺节目的精彩内容。',
      },
    },
  ],
};

const milestones = [
  { year: '2018', title: '平台创立', desc: '天天影视正式成立，专注于综艺节目内容聚合与分发' },
  { year: '2019', title: '内容扩展', desc: '综艺节目数量突破500部，覆盖真人秀、脱口秀、音乐综艺等多个品类' },
  { year: '2020', title: '技术升级', desc: '全面升级播放技术，实现1080P高清流畅播放，CDN多节点加速' },
  { year: '2021', title: '用户破千万', desc: '月活跃用户突破1000万，成为国内领先的综艺节目在线观看平台' },
  { year: '2022', title: '版权合作', desc: '与多家国内顶级卫视和视频平台达成版权合作，正版内容全覆盖' },
  { year: '2023', title: '智能推荐', desc: '上线AI智能推荐系统，根据用户的观看偏好精准推荐综艺内容' },
  { year: '2024', title: '国际化布局', desc: '开始布局海外市场，为全球华人提供中文综艺节目在线观看服务' },
  { year: '2025', title: '生态完善', desc: '构建完整的综艺内容生态，涵盖观看、互动、社区、资讯等功能' },
  { year: '2026', title: 'AI赋能', desc: '引入AI技术全面升级用户体验，智能字幕、个性化推荐全面提升' },
];

export default function About() {
  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />

        <main className="flex-1">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
            <div className="max-w-[1024px] mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">关于天天影视</h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                天天影视是国内领先的综艺节目在线观看平台，致力于为用户提供最新、最全、最优质的综艺内容。从真人秀到脱口秀，从音乐综艺到竞技挑战，我们汇聚了最受欢迎的综艺节目，让每一位用户都能在这里找到自己喜欢的综艺内容。
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-[1024px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">品牌故事</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20media%20company%20office%20interior%20with%20video%20screens%20showing%20entertainment%20content%20creative%20workspace%20warm%20lighting%20collaborative%20atmosphere%20professional%20photography%20clean%20composition&width=800&height=500&seq=about-brand&orientation=landscape"
                    alt="天天影视品牌故事"
                    title="天天影视 - 品牌故事"
                    className="w-full rounded-lg"
                  />
                </div>
                <div className="space-y-4">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    天天影视成立于2018年，创始团队由一群热爱综艺节目的年轻人组成。我们深知现代人的生活节奏越来越快，工作压力越来越大，而综艺节目正是缓解压力、放松心情的最佳方式之一。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    我们的使命是让每一个热爱综艺的人都能方便快捷地观看到自己喜欢的节目。无论是热门真人秀，还是搞笑脱口秀，无论是在电脑、平板还是手机上，天天影视都能为你提供流畅高清的观看体验。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    经过多年的发展，天天影视已经成为拥有数千万用户的综艺节目在线观看平台。我们持续与国内顶级卫视和制作公司合作，不断丰富内容资源，优化观看体验，让"追综艺，来天天"成为每一位综艺爱好者的自然选择。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1024px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-colors">
                          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium mb-2">
                            {item.year}
                          </span>
                          <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-xs text-foreground-500">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center justify-center w-8 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-100"></div>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-[1024px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3 text-secondary-500">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 mb-4 leading-relaxed">
                    "天天影视真的是追综艺的神器！更新速度超快，画质清晰，一点也不卡。现在每天下班回家最大的乐趣就是打开天天影视看最新的综艺节目。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 text-xs font-medium">
                      李
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">李先生</p>
                      <p className="text-[10px] text-foreground-500">忠实用户 · 3年</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3 text-secondary-500">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 mb-4 leading-relaxed">
                    "综艺节目种类齐全，分类也很清晰。我特别喜欢脱口秀和搞笑综艺，天天影视上的资源比其他平台多多了，强烈推荐！"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-medium">
                      王
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">王女士</p>
                      <p className="text-[10px] text-foreground-500">综艺达人 · 2年</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3 text-secondary-500">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 mb-4 leading-relaxed">
                    "作为一个综艺迷，天天影视满足了我对综艺节目的所有需求。免费、高清、更新快、内容全，可以说是目前最好的综艺节目在线观看平台了。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-medium">
                      张
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">张先生</p>
                      <p className="text-[10px] text-foreground-500">资深用户 · 4年</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1024px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">常见问题</h2>
              <div className="max-w-2xl mx-auto space-y-4">
                <details className="bg-background-50 border border-background-200/70 rounded-lg p-5 group cursor-pointer">
                  <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    天天影视是什么？
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                    天天影视是一个专注于综艺节目的在线观看平台，提供热门综艺、真人秀、脱口秀、音乐综艺、竞技综艺等各类综艺节目的高清在线观看服务。我们的内容覆盖了国内主流卫视和网络平台的综艺节目资源。
                  </p>
                </details>
                <details className="bg-background-50 border border-background-200/70 rounded-lg p-5 group cursor-pointer">
                  <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    综艺节目是免费的吗？
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                    是的，天天影视提供免费的综艺节目在线观看服务。我们致力于让每一位用户都能轻松享受优质的综艺内容，无需付费即可观看高清综艺节目。我们通过广告等方式维持平台运营，确保用户可以免费获得优质的观看体验。
                  </p>
                </details>
                <details className="bg-background-50 border border-background-200/70 rounded-lg p-5 group cursor-pointer">
                  <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    多久更新一次？
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                    天天影视每天都会更新最新的综艺节目内容。热门综艺节目一般在上线后24小时内即可在天天影视观看，确保用户不会错过任何精彩内容。我们建立了高效的更新机制，确保内容的及时性。
                  </p>
                </details>
                <details className="bg-background-50 border border-background-200/70 rounded-lg p-5 group cursor-pointer">
                  <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    支持哪些设备观看？
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                    天天影视支持电脑、平板和手机等多种设备在线观看。只需使用浏览器访问天天影视网站，即可随时随地享受综艺节目的精彩内容。我们的网站采用了响应式设计，可以自动适配不同屏幕尺寸。
                  </p>
                </details>
                <details className="bg-background-50 border border-background-200/70 rounded-lg p-5 group cursor-pointer">
                  <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    如何联系客服？
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                    您可以通过以下方式联系我们：邮箱 contact@tiantian.tv，客服电话 400-888-6666（工作日9:00-18:00），或通过官方微博和微信公众号给我们留言，我们的客服团队会尽快回复您。
                  </p>
                </details>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-[1024px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@tiantian.tv</p>
                  <p className="text-xs text-foreground-400 mt-0.5">24小时内回复</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-3">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h3>
                  <p className="text-xs text-foreground-500">400-888-6666</p>
                  <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 18:00</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">北京市朝阳区望京SOHO</p>
                  <p className="text-xs text-foreground-400 mt-0.5">T2栋 15层 1501室</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}